import React, { useEffect, useState } from "react";
import { api, i18n } from "@/lib/api";

export default function AdminPortal({ language }) {
  const t = i18n[language];
  const [metrics, setMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setBusy(true);
    setError("");
    try {
      const [m, u] = await Promise.all([api.adminMetrics(), api.adminUsers()]);
      setMetrics(m);
      setUsers(u.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Admin data unavailable");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const adjustCredits = async (userId, baseCredits) => {
    const val = window.prompt(language === "es" ? "Nuevo balance base de creditos:" : "New base credit balance:", baseCredits);
    if (val === null) return;
    const n = parseInt(val, 10);
    if (Number.isNaN(n)) return;
    try {
      await api.adminAdjust({ user_id: userId, base_credits_remaining: n });
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Adjust failed");
    }
  };

  const toggleAdmin = async (userId, current) => {
    try {
      await api.adminAdjust({ user_id: userId, is_admin: !current });
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Adjust failed");
    }
  };

  return (
    <div data-testid="admin-portal">
      <div className="h-eyebrow">{t.admin}</div>
      <h2 className="h-display" style={{ fontSize: "1.4rem", margin: "0.35rem 0 1.25rem" }}>
        {language === "es" ? "Centro de Comando Administrativo" : "Administrative Command Center"}
      </h2>

      {error && <p className="txt-danger" data-testid="admin-error">{error}</p>}

      {metrics && (
        <div data-testid="admin-metrics" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "1.5rem", marginBottom: "2rem" }}>
          <div>
            <div className="h-eyebrow">{language === "es" ? "Suscriptores Activos" : "Active Subscribers"}</div>
            <div className="h-display" style={{ fontSize: "2rem", marginTop: "0.25rem" }} data-testid="admin-active-subscribers">
              {metrics.active_subscribers}
            </div>
          </div>
          <div>
            <div className="h-eyebrow">MRR</div>
            <div className="h-display" style={{ fontSize: "2rem", marginTop: "0.25rem" }} data-testid="admin-mrr">
              ${metrics.monthly_recurring_revenue_usd?.toFixed(2)}
            </div>
          </div>
          <div>
            <div className="h-eyebrow">{language === "es" ? "Generaciones" : "Generations"}</div>
            <div className="h-display" style={{ fontSize: "2rem", marginTop: "0.25rem" }} data-testid="admin-total-generations">
              {metrics.total_generations}
            </div>
          </div>
          <div>
            <div className="h-eyebrow">{language === "es" ? "Recargas" : "Top-ups"}</div>
            <div className="h-display" style={{ fontSize: "2rem", marginTop: "0.25rem" }} data-testid="admin-topups">
              {metrics.topup_pack_purchases}
            </div>
          </div>
        </div>
      )}

      <div className="line" />
      <div className="h-eyebrow" style={{ marginBottom: "0.5rem" }}>{language === "es" ? "Usuarios" : "Users"}</div>

      {busy ? <p className="txt-mute live-dot">Loading</p> : null}

      <div data-testid="admin-user-table">
        {users.map((u) => (
          <div key={u.id} style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 0.7fr auto", gap: "1rem", padding: "0.7rem 0", borderBottom: "1px solid var(--line)", alignItems: "center", fontSize: "0.92rem" }}>
            <div>
              <div>{u.email}</div>
              <div className="kbd">{u.full_name || ""}</div>
            </div>
            <div className="kbd">{u.subscription_status}</div>
            <div className="kbd">base {u.base_credits_remaining} / topup {u.purchased_credits_remaining}</div>
            <div className="kbd">{u.is_admin ? "ADMIN" : ""}</div>
            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
              <button
                data-testid={`admin-adjust-credits-${u.id}`}
                className="btn-link"
                onClick={() => adjustCredits(u.id, u.base_credits_remaining)}
              >
                {language === "es" ? "Creditos" : "Credits"}
              </button>
              <button
                data-testid={`admin-toggle-admin-${u.id}`}
                className="btn-link"
                onClick={() => toggleAdmin(u.id, u.is_admin)}
              >
                {u.is_admin ? (language === "es" ? "Quitar admin" : "Revoke admin") : (language === "es" ? "Hacer admin" : "Make admin")}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
